import { Form, Formik } from "formik";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useHasPermission } from "../../common/Hooks";
import Toggle from "../controls/Toggle";
import Utils from "../../utils/utils";
import Product from "../../services/product";
import PubSub from "../../services/PubSub";
import ConfirmationPrompt from "../controls/ConfirmationPrompt";
import MigrateProductSettings from "./MigrateProductSettings";
import TextBox from "../controls/form/TextBox";
import UploadArea from "../controls/UploadArea";
import SettingsModal from "../settings/SettingsModal";
import { Button } from "react-bootstrap";
import Toolbar from "../controls/Toolbar";
import CheckBox from "../controls/form/Checkbox";

const ProductVersionDetails = () => {
    const { id } = useParams();
    const [showMigrateProductSettingsModal, setShowMigrateProductSettingsModal] = useState(false);

    const [translate] = useTranslation();
    const history = useHistory();
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "domain"
    });

    const [showSettings, setShowSettings] = useState(false);
    const [settingsSetId, setSettingsSetId] = useState("");

    const [productVersionData, setProductVersionData] = useState();

    const [versionIdForDelete, setVersionIdForDelete] = useState("");
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showSaveButton, setShowSaveButton] = useState(false);

    const [editorMode, setEditorMode] = useState(false);
    const [versionUsed, setVersionUsed] = useState(false);

    const getProductVersion = useCallback(async (versionId) => {
        const response = await Product.getProductVersion(versionId);

        if (!response.error) {
            setProductVersionData(response.data);
            setEditorMode(false);
            setSettingsSetId(response.data.settingsSet);
        }

        const isVersionUsed = await Product.isProductVersionAssigned(versionId);
        setVersionUsed(isVersionUsed.data);
    }, []);

    useEffect(() => {
        getProductVersion(id);
    }, [id, getProductVersion]);

    const deleteProductVersion = useCallback(async () => {
        const response = await Product.deleteProductVersion(versionIdForDelete);
        if (!response.error) {
            setVersionIdForDelete("");
            PubSub.publish("refresh-product-list");
            history.push("/products");
            setShowConfirmation(false);
        }
    }, [history, versionIdForDelete]);

    const processDelete = useCallback(() => {
        setVersionIdForDelete(id);
        setShowConfirmation(true);
    }, [id]);

    const updateProductVersion = useCallback(async (values) => {
        const response = await Product.updateProductVersion(id, values);
        if (!response.error) {
            PubSub.publish("refresh-product-list");
            setShowSaveButton(false);
        }
    }, [id]);

    const renderToolbar = useMemo(() => {
        return (
            <Toolbar
                buttons={[
                    {
                        name: "Save",
                        title: translate("OTM_SAVE"),
                        type: "submit",
                        hidden: !showSaveButton
                    },
                    {
                        name: "Delete",
                        title: translate("OTM_DELETE"),
                        onClick: processDelete
                    }
                ]}
                heading={translate("OTM_PRODUCT_DETAILS")} />
        );
    }, [translate, showSaveButton, processDelete]);

    const renderEditorModeToggle = useMemo(() => {
        return (
            <div className="col-6 col-lg-4 mt-3">
                <label htmlFor="editorMode" className="d-block form-label h6 mb-0">{translate("OTM_EDITOR_MODE")}</label>
                <div className="form-check form-switch">
                    <Toggle
                        id="setEditorMode"
                        onChange={() => setEditorMode(!editorMode)}
                        defaultState={editorMode}
                        disabled={!permissions.update}
                    />
                </div>
            </div>
        );
    }, [translate, editorMode, permissions.update, setEditorMode]);

    const renderProductVersionDetails = useMemo(() => (
        <div className="col-12 mt-3">
            <Formik initialValues={{
                name: productVersionData?.productVersion ?? "",
                buildServer: productVersionData?.buildServer ?? "",
                releaseNotes: productVersionData?.releaseNotes ?? "",
                installAsAppName: productVersionData?.installAsAppName ?? "",
                onPrem: productVersionData?.onPrem ?? false,
                isForms: productVersionData?.isForms ?? false,
                livePreviewEnabled: productVersionData?.livePreviewEnabled ?? false,
                enableConfigUiEditor: productVersionData?.enableConfigUiEditor ?? false,
                enableOverrideLogoIcon: productVersionData?.enableOverrideLogoIcon ?? false
            }}
                onSubmit={updateProductVersion}
                enableReinitialize>
                <Form onChange={() => {
                    setShowSaveButton(true);
                }}>
                    <div className="card h-100">
                        <div className="card-header border-bottom">
                            {renderToolbar}
                        </div>
                        <div className="card-body">
                            <div className="row mt-n3 align-items-end">
                                <div className="row">
                                    <TextBox css="col-12 mt-3" label={translate("OTM_NAME")} type="text" name="name" />
                                    <TextBox css="col-6 mt-3" label={translate("OTM_BUILD_SERVER")} type="text" name="buildServer" />
                                    <TextBox css="col-6 mt-3" label={translate("OTM_INSTALL_AS_APP_NAME")} type="text" name="installAsAppName" />
                                    <TextBox css="col-12 mt-3" label={translate("OTM_RELEASE_NOTES")} type="text" name="releaseNotes" />

                                    <div className="col mt-3">
                                        <CheckBox
                                            label={translate("OTM_ON_PREM")}
                                            name="onPrem"
                                            disabled={!permissions.update}
                                        />
                                    </div>
                                    <div className="col mt-3">
                                        <CheckBox
                                            label={translate("OTM_IS_FORMS")}
                                            name="isForms"
                                            disabled={!permissions.update}
                                        />
                                    </div>
                                    <div className="col mt-3">
                                        <CheckBox
                                            label={translate("OTM_LIVE_PREVIEW")}
                                            name="livePreviewEnabled"
                                            disabled={!permissions.update}
                                        />
                                    </div>
                                    <div className="col mt-3">
                                        <CheckBox
                                            label={translate("OTM_ENABLE_CONFIG_UI_EDITOR")}
                                            name="enableConfigUiEditor"
                                            disabled={!permissions.update}
                                        />
                                    </div>
                                    <div className="col mt-3">
                                        <CheckBox
                                            label={translate("OTM_ENABLE_OVERRIDE_LOGO_ICON")}
                                            name="enableOverrideLogoIcon"
                                            disabled={!permissions.update}
                                        />
                                    </div>
                                </div>

                                <div className="col-6 col-lg-4 mt-3">
                                    <Button
                                        disabled={!permissions.create}
                                        variant="primary"
                                        onClick={() => setShowMigrateProductSettingsModal(true)}>
                                        {translate("OTM_INITIALISE_FROM")}
                                    </Button>
                                </div>
                                <div className="col-6 col-lg-4 mt-3">
                                    <Button
                                        disabled={!permissions.read}
                                        variant="primary"
                                        onClick={() => setShowSettings(true)}>
                                        {permissions.update ? translate("OTM_EDIT_SETTINGS") : translate("OTM_VIEW_SETTINGS")}
                                    </Button>
                                </div>
                                <div className="col-12 mt-3">
                                    <UploadArea
                                        heading={translate("OTM_UPLOAD_TEMPALTE_METADATA")}
                                        disabled={!permissions.update}
                                        parentId={id}
                                        uploadCallback={Product.updateTemplateMetadata}
                                        responseCallback={() => { }}
                                    />
                                </div>
                                {renderEditorModeToggle}
                            </div>
                            {
                                settingsSetId &&
                                <SettingsModal
                                    show={showSettings}
                                    onCancel={() => setShowSettings(false)}
                                    settingsSetId={settingsSetId}
                                    permissions={permissions}
                                    context={{ type: 'productVersion', headerText: `${productVersionData?.product?.name} ${productVersionData?.productVersion}`, versionUsed, editorMode, objectId: Utils.getDomain() }} />
                            }
                            <ConfirmationPrompt
                                show={showConfirmation}
                                message={translate("OTM_DELETE_PRODUCT_VERSION")}
                                onConfirm={deleteProductVersion}
                                onCancel={() => setShowConfirmation(false)}
                            />
                            <MigrateProductSettings
                                show={showMigrateProductSettingsModal}
                                onCancel={() => setShowMigrateProductSettingsModal(false)}
                            />
                        </div>
                    </div>
                </Form>
            </Formik>
        </div>
    ), [showConfirmation, deleteProductVersion, productVersionData, settingsSetId, showSettings, editorMode, permissions, renderEditorModeToggle, renderToolbar, updateProductVersion,
        showMigrateProductSettingsModal, translate, versionUsed, id]);

    return (
        <div style={{ overflowX: 'hidden', overflowY: 'auto' }}>
            {renderProductVersionDetails}
        </div>
    );
};

export default ProductVersionDetails;